import React, {useEffect, useMemo, useState} from "react";
import {Dialog} from "primereact/dialog";
import {InputText} from "primereact/inputtext";
import {Dropdown} from "primereact/dropdown";
import {MultiSelect} from "primereact/multiselect";
import Icon from "../../../system/components/icon";
import {RichTextEditor} from "../../../system/components/rich-text";
import {TASK_STATUS, addDays, errorText, sameTitle} from "./meetings-labels";
import {DayPicker} from "./meetings-ui";

// «ثبت خروجی جلسه» (spec 5.7): 1) the decision — title, description / success measure, owner, due date, goal or
// milestone; 2) the action — a new project task, an existing project task, or no task for now. The result is not
// asked here: it is written later, after the real effect is seen.

export const TASK_MODES = [
    {value: "new", label: "ساخت کار جدید", icon: "add_task"},
    {value: "existing", label: "پیوند کار موجود", icon: "link"},
    {value: "none", label: "فعلاً بدون کار", icon: "do_not_disturb_on"},
];

export function personOptions(people, keys) {
    const byKey = new Map(people.map((item) => [item.key, item]));
    return (keys || people.map((item) => item.key)).map((key) => byKey.get(key)).filter(Boolean).map((item) => ({value: item.key, label: item.name}));
}

export function targetOptions(targets) {
    return targets.map((target) => ({value: `${target.type}:${target.id}`, label: `${target.type === "goal" ? "هدف" : "مایلستون"} · ${target.title}`}));
}

// the project's milestones, for linking a decision's tasks (that is what counts in milestone progress)
export const milestoneOptions = (targets) => targets.filter((target) => target.type === "milestone").map((target) => ({value: target.id, label: target.title}));

export const targetFromValue = (value) => {
    if (!value) return null;
    const [type, ...rest] = value.split(":");
    return {type, id: rest.join(":")};
};

export default function OutputDialog({visible, onHide, meeting, tab, onSubmit}) {
    const [draft, setDraft] = useState({});
    const [mode, setMode] = useState("new");
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);
    const people = useMemo(() => personOptions(tab.people, tab.members), [tab]);
    const meKey = tab.permissions?.meKey;

    useEffect(() => {
        if (!visible || !meeting) return;
        const owner = tab.members.includes(meKey) ? meKey : tab.members[0] || "";
        setDraft({title: "", description: "", owner, dueDate: addDays(meeting.date, 7), target: "", taskTitle: "", taskAssignee: owner, taskDueDate: addDays(meeting.date, 7), taskMilestones: [], taskKey: ""});
        setMode(tab.permissions?.canCreateTask ? "new" : "existing");
        setError("");
        setSaving(false);
    }, [visible, meeting, tab, meKey]);

    const set = (field) => (value) => setDraft((current) => ({...current, [field]: value}));
    const duplicate = mode === "new" && draft.taskTitle?.trim() ? tab.tasks.find((task) => sameTitle(task.title, draft.taskTitle)) : null;
    const taskOptions = useMemo(() => tab.tasks.map((task) => ({value: task.key, label: task.title, task})), [tab.tasks]);
    const nameOf = (key) => tab.people.find((item) => item.key === key)?.name || "بدون مسئول";

    const submit = async () => {
        setError("");
        if (!draft.title?.trim()) return setError("عنوان تصمیم را بنویسید.");
        if (mode === "new" && !draft.taskTitle?.trim()) return setError("عنوان کار را بنویسید.");
        if (mode === "new" && duplicate) return setError("کاری با همین عنوان در پروژه هست؛ «پیوند کار موجود» را انتخاب کنید.");
        if (mode === "existing" && !draft.taskKey) return setError("یک کار موجود را انتخاب کنید.");
        setSaving(true);
        try {
            await onSubmit({
                title: draft.title.trim(),
                description: draft.description,
                owner: draft.owner || null,
                dueDate: draft.dueDate || null,
                target: targetFromValue(draft.target),
                taskMode: mode,
                task: mode === "new"
                    ? {title: draft.taskTitle.trim(), assignee: draft.taskAssignee || null, dueDate: draft.taskDueDate || null, milestones: draft.taskMilestones || []}
                    : mode === "existing" ? {key: draft.taskKey} : null,
            });
        } catch (requestError) {
            setError(errorText(requestError));
            setSaving(false);
        }
    };

    if (!meeting) return null;
    return (
        <Dialog visible={visible} onHide={onHide} modal dir="rtl" className="rtl w-11 md:w-8 lg:w-6 xl:w-5 mt-meeting-dialog"
                header={(
                    <div className="flex align-items-center gap-3">
                        <span className="mt-head-icon"><Icon icon="mi mi:note_add"/></span>
                        <div>
                            <span className="pd-tiny text-500">{`خروجی جلسه · ${meeting.title}`}</span>
                            <b className="text-base block">تصمیم را به پیگیری تبدیل کن</b>
                            <p className="pd-sub">تصمیم، کار و نتیجه جدا ثبت می‌شوند تا مسیر اقدام روشن بماند.</p>
                        </div>
                    </div>
                )}
                footer={(
                    <div className="flex justify-content-end gap-2">
                        <button type="button" className="pd-btn" onClick={onHide}>انصراف</button>
                        <button type="button" className="pd-btn primary" disabled={saving} onClick={submit}>
                            <Icon icon="mi mi:done_all" className="text-sm"/>{saving ? "در حال ثبت…" : "ثبت تصمیم و پیگیری"}
                        </button>
                    </div>
                )}>
            <div className="flex flex-column gap-3">
                <section className="mt-form-section">
                    <b className="text-xs"><span className="mt-step">۱</span> تصمیم گرفته‌شده</b>
                    <label className="mt-field"><span>عنوان تصمیم <b className="tone-red">*</b></span>
                        <InputText autoFocus value={draft.title || ""} onChange={(event) => set("title")(event.target.value)} placeholder="مثلاً کاهش زمان پاسخ سفارش" className="w-full text-xs" maxLength={200}/>
                    </label>
                    <div className="mt-field">شرح یا معیار موفقیت
                        <RichTextEditor value={draft.description || ""} onChange={set("description")} placeholder="چه چیزی انتخاب یا تصویب شد؟ موفقیت را با چه می‌سنجیم؟" minHeight="4.5rem" ariaLabel="شرح تصمیم"/>
                    </div>
                    <div className="mt-form-grid">
                        <label className="mt-field">مسئول پیگیری تصمیم
                            <Dropdown value={draft.owner} options={people} onChange={(event) => set("owner")(event.value)} filter placeholder="انتخاب عضو" className="w-full text-xs" panelClassName="rtl text-xs" showClear/>
                        </label>
                        <label className="mt-field">موعد اجرا
                            <DayPicker value={draft.dueDate} onChange={(day) => set("dueDate")(day)}/>
                        </label>
                    </div>
                    <label className="mt-field"><span>هدف یا مایلستون مرتبط <span className="pd-tiny text-500 font-normal">(فقط مرجع؛ در پیشرفت مایلستون حساب نمی‌شود)</span></span>
                        <Dropdown value={draft.target || null} options={targetOptions(tab.targets)} onChange={(event) => {
                            const value = event.value || "";
                            setDraft((current) => ({...current, target: value, taskMilestones: value.startsWith("milestone:") && !(current.taskMilestones || []).length ? [value.slice(10)] : current.taskMilestones}));
                        }} filter showClear placeholder="بدون پیوند" emptyMessage="هدف یا مایلستونی برای این پروژه ثبت نشده" className="w-full text-xs" panelClassName="rtl text-xs"/>
                    </label>
                </section>

                <section className="mt-form-section">
                    <div className="flex align-items-start justify-content-between gap-2 flex-wrap">
                        <div>
                            <b className="text-xs"><span className="mt-step">۲</span> اقدام اجرایی مرتبط</b>
                            <p className="pd-tiny text-500 m-0 mt-1">کار جدید بسازید، کار موجود را پیوند دهید یا فعلاً کار نسازید.</p>
                        </div>
                        <div className="pd-segment" role="radiogroup" aria-label="نوع اقدام">
                            {TASK_MODES.map((item) => (
                                <button key={item.value} type="button" role="radio" aria-checked={mode === item.value} className={mode === item.value ? "is-active" : ""}
                                        disabled={(item.value === "new" && !tab.permissions?.canCreateTask) || (item.value === "existing" && !tab.tasks.length)}
                                        onClick={() => setMode(item.value)}>
                                    {item.label}
                                </button>
                            ))}
                        </div>
                    </div>
                    {mode === "new" && (
                        <>
                            <label className="mt-field"><span>عنوان کار <b className="tone-red">*</b></span>
                                <InputText value={draft.taskTitle || ""} onChange={(event) => set("taskTitle")(event.target.value)} placeholder="اقدامی که این تصمیم را اجرا می‌کند" className="w-full text-xs" maxLength={200}/>
                            </label>
                            {duplicate && <p className="mt-warning">کاری با همین عنوان از قبل در پروژه وجود دارد؛ به‌جای ساخت مورد تکراری، «پیوند کار موجود» را انتخاب کنید.</p>}
                            <div className="mt-form-grid">
                                <label className="mt-field">مسئول کار
                                    <Dropdown value={draft.taskAssignee} options={people} onChange={(event) => set("taskAssignee")(event.value)} filter placeholder="انتخاب عضو" className="w-full text-xs" panelClassName="rtl text-xs" showClear/>
                                </label>
                                <label className="mt-field">موعد کار
                                    <DayPicker value={draft.taskDueDate} onChange={(day) => set("taskDueDate")(day)}/>
                                </label>
                            </div>
                            <label className="mt-field">مایلستون کار
                                <MultiSelect value={draft.taskMilestones || []} options={milestoneOptions(tab.targets)} onChange={(event) => set("taskMilestones")(event.value)} placeholder="بدون مایلستون" display="chip" className="w-full text-xs" panelClassName="rtl text-xs" emptyMessage="مایلستونی برای این پروژه ثبت نشده"/>
                            </label>
                            <p className="pd-tiny text-500 m-0">کار در فهرست کارهای همین پروژه ساخته می‌شود؛ سیستم جداگانه‌ای برای کارهای جلسه نیست.</p>
                        </>
                    )}
                    {mode === "existing" && (
                        <>
                            <label className="mt-field">کار موجود پروژه
                                <Dropdown value={draft.taskKey} options={taskOptions} onChange={(event) => set("taskKey")(event.value)} filter placeholder="انتخاب کار موجود…" className="w-full text-xs" panelClassName="rtl text-xs"
                                          itemTemplate={(option) => <span className="text-xs">{`${option.label} · ${nameOf(option.task.assignee)} · ${TASK_STATUS[option.task.status]?.label || ""}`}</span>}/>
                            </label>
                            <p className="pd-tiny text-500 m-0">همان کار با مسئول و وضعیت فعلی‌اش به این تصمیم وصل می‌شود؛ کار تازه‌ای ساخته نمی‌شود.</p>
                        </>
                    )}
                    {mode === "none" && <p className="pd-tiny text-500 m-0">تصمیم بدون کار ثبت می‌شود؛ هر زمان لازم شد از جزئیات تصمیم کار بسازید یا کار موجود را پیوند دهید.</p>}
                </section>

                <div className="pd-bg tone-blue border-round-xl p-2 flex gap-2 align-items-center">
                    <Icon icon="mi mi:help" className="tone-blue text-sm"/>
                    <span className="pd-tiny text-700">نتیجه پس از انجام کار ثبت می‌شود؛ تکمیل کار، موفقیت تصمیم را خودکار اعلام نمی‌کند.</span>
                </div>
                {error && <p className="text-xs tone-red m-0" role="alert">{error}</p>}
            </div>
        </Dialog>
    );
}
